import { Component } from "@theme/component";
import { QuantitySelectorUpdateEvent } from "@theme/events";

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement[]} quantityButtons
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Initializes the component (called on connect and update).
   */
  #initialize() {
    this.#setupInputListener();
    this.#setupMaxObserver();
    this.updateButtonStates();
  }

  /**
   * Called when the element is connected to the document's DOM.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.quantityInput?.removeEventListener("input", this.#handleInput);
    this.#maxObserver?.disconnect();
  }

  /**
   * Sets up the input event listener to prevent typing values above max.
   */
  #setupInputListener() {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    // Remove existing listener if any (safe even if not added)
    quantityInput.removeEventListener("input", this.#handleInput);
    // Add the listener
    quantityInput.addEventListener("input", this.#handleInput);
  }

  /**
   * Sets up a MutationObserver to watch for changes to the max attribute.
   */
  #setupMaxObserver() {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    // Disconnect existing observer if any
    if (this.#maxObserver) {
      this.#maxObserver.disconnect();
    }

    // Create observer to watch for max attribute changes
    this.#maxObserver = new MutationObserver(() => {
      this.updateButtonStates();
    });

    // Observe changes to the max attribute
    this.#maxObserver.observe(quantityInput, {
      attributes: true,
      attributeFilter: ["max"],
    });
  }

  /**
   * Handles input events to prevent typing values above max.
   * @param {InputEvent} event - The input event.
   */
  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const { quantityInput } = this.refs;
    const inputValue = parseInt(event.target.value);
    const { max, hasValidMax } = this.#getNumericValues();

    // If max is set and value exceeds it, clamp to max
    if (hasValidMax && !isNaN(inputValue) && inputValue > max) {
      quantityInput.value = max.toString();
      this.#onQuantityChange(event);
    }
  };

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { max, value, hasValidMax } = this.#getNumericValues();

    // Don't increase if already at max (only if max is valid)
    if (hasValidMax && value >= max) {
      return;
    }

    quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !== quantityInput
    )
      return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    if (event.target instanceof HTMLInputElement) {
      quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.updateButtonStates();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(quantityInput.dataset.cartLine)
      )
    );
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value, hasValidMax } = this.#getNumericValues();

    if (!isNaN(min) && !isNaN(value) && value < min) {
      quantityInput.value = min.toString();
    }
    if (hasValidMax && !isNaN(value) && value > max) {
      quantityInput.value = max.toString();
    }
  };

  /**
   * Gets numeric values from the quantity input.
   * @returns {{min: number, max: number, value: number, hasValidMax: boolean}}
   */
  #getNumericValues() {
    const { quantityInput } = this.refs;
    const min = parseInt(quantityInput.min);
    const max = parseInt(quantityInput.max);
    const value = parseInt(quantityInput.value) || 0;
    return {
      min,
      max,
      value,
      hasValidMax: !isNaN(max) && max > 0,
    };
  }

  /**
   * Updates the disabled state and class for a button.
   * @param {HTMLButtonElement | undefined} button - The button to update.
   * @param {boolean} shouldDisable - Whether the button should be disabled.
   */
  #updateButtonState(button, shouldDisable) {
    if (!button) return;
    button.disabled = shouldDisable;
    button.classList.toggle("disabled", shouldDisable);
  }

  /**
   * MutationObserver instance to watch for max attribute changes.
   * @type {MutationObserver | undefined}
   */
  #maxObserver;

  /**
   * Updates the disabled state of the quantity buttons based on min/max rules.
   * Made public so it can be called from outside the component.
   */
  updateButtonStates = () => {
    const { quantityInput, quantityButtons } = this.refs;
    if (!quantityInput || !quantityButtons || quantityButtons.length < 2) {
      return;
    }

    const { min, max, value, hasValidMax } = this.#getNumericValues();

    // Find minus and plus buttons
    const minusButton =
      quantityButtons.find((btn) => btn.name === "minus") || quantityButtons[0];
    const plusButton =
      quantityButtons.find((btn) => btn.name === "plus") ||
      quantityButtons[quantityButtons.length - 1];

    // Update button states
    this.#updateButtonState(minusButton, !isNaN(min) && value <= min);
    this.#updateButtonState(plusButton, hasValidMax && value >= max);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get("quantity-selector-component")) {
  customElements.define(
    "quantity-selector-component",
    QuantitySelectorComponent
  );
}
